/* ============================================================
   JMO Form Success Guard
   ============================================================
   Shopify's `{% if form.posted_successfully? %}` Liquid check is
   TYPE-scoped, not id-scoped. When ANY `{% form 'contact' %}` on
   the JMO theme is submitted successfully, the post-redirect page
   has `?contact_posted=true&form_type=contact`. Shopify's renderer
   then sets `form.posted_successfully?` = TRUE for *every* contact
   form on the resulting page — not just the one that was actually
   posted.

   That causes cross-form leaks like:
     - Customer opens the quote drawer on /pages/contact, submits it
       → contact-page form ALSO shows its inline success block
     - Customer submits the contact form → quote drawer would have
       shown its success card (fixed separately in jmo-quote.js
       via the jmoQuoteFormSubmitted flag)
     - Customer navigates directly to /pages/contact?contact_posted=true
       (e.g. shared link, back button) → contact success shows even
       though no submission happened

   THIS GUARD prevents that by stamping a sessionStorage marker at
   submit time identifying *which* form initiated the post. On the
   next page load, any inline success block whose form-class doesn't
   match the marker is hidden.

   Each form is identified by the wrapping <form> element's class:
     - jmo-quote__form      → 'quote'      (consumed by jmo-quote.js)
     - jmo-contact__form    → 'contact'
     - jmo-bulk__form       → 'bulk'
     - jmo-cta__form        → 'newsletter'

   The marker self-cleans on any page load without contact_posted=true
   (so an abandoned challenge can't carry the flag into a later submit).
   ============================================================ */
(function () {
  if (window.__jmoFormSuccessGuardInit) return;
  window.__jmoFormSuccessGuardInit = true;

  var STORAGE_KEY = 'jmoFormSubmitted';

  // Map form CSS class → marker value
  var FORM_CLASS_MAP = {
    'jmo-quote__form': 'quote',
    'jmo-contact__form': 'contact',
    'jmo-bulk__form': 'bulk',
    'jmo-cta__form': 'newsletter'
  };

  // Map marker value → success-block selector to keep visible (everything else hidden)
  var SUCCESS_BLOCK_SELECTORS = {
    contact: '.jmo-contact__form-success',
    bulk: '.jmo-bulk__form-success',
    newsletter: '.jmo-cta__success'
    // quote drawer is gated by its own data-jmo-quote-success + flag in jmo-quote.js
  };

  function detectFormType(form) {
    if (!form || !form.classList) return null;
    for (var className in FORM_CLASS_MAP) {
      if (form.classList.contains(className)) return FORM_CLASS_MAP[className];
    }
    return null;
  }

  // 1. CAPTURE-phase submit listener: stamp marker BEFORE any other handler can preventDefault.
  //    Capture phase ensures we get it even if another listener stops propagation.
  document.addEventListener('submit', function (e) {
    var formType = detectFormType(e.target);
    if (!formType) return;
    sessionStorage.setItem(STORAGE_KEY, formType);
  }, true);

  // 2. On page load: validate any inline success blocks against the marker.
  //    The contact_posted=true URL param is what triggers Shopify to server-render
  //    these success blocks. We hide any that don't match the marker.
  function validateInlineSuccess() {
    var params = new URLSearchParams(window.location.search);
    // Verified live (john@jmotechnology.com 2026-06-02): Shopify DROPS the
    // `form_type=contact` param when redirecting back from `/challenge` after
    // successful CAPTCHA verification — the final URL is just `?contact_posted=true`.
    // So we accept the bare *_posted=true params alone as the success signal.
    var hasSuccessParam =
      params.get('contact_posted') === 'true' ||
      params.get('customer_posted') === 'true';

    if (!hasSuccessParam) {
      // No success in URL → any inline success block in DOM is stale or never-rendered.
      // EXCEPT: don't wipe the marker while we're on the /challenge page itself,
      // because that's an intermediate step in the success flow (see jmo-quote.js
      // for the same reason). The marker must survive across the CAPTCHA round-trip.
      if (window.location.pathname.indexOf('/challenge') !== 0) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
      return;
    }

    var marker = sessionStorage.getItem(STORAGE_KEY);

    // Hide every success block that doesn't match the marker.
    Object.keys(SUCCESS_BLOCK_SELECTORS).forEach(function (type) {
      var block = document.querySelector(SUCCESS_BLOCK_SELECTORS[type]);
      if (!block) return;
      if (marker !== type) {
        // This block was server-rendered because Shopify saw contact_posted=true,
        // but a different form (or no form) actually initiated the post — suppress it.
        block.style.display = 'none';
        block.setAttribute('aria-hidden', 'true');
        block.hidden = true;
      }
    });

    // Consume the marker — even if no matching block was on this page, we're done with it.
    sessionStorage.removeItem(STORAGE_KEY);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', validateInlineSuccess);
  } else {
    validateInlineSuccess();
  }
})();
